import React, { useContext, useEffect, useState } from 'react';
import { style } from 'typestyle';
import Modal from 'react-modal';
import { useTranslation } from 'react-i18next';
import * as styles from './modal.styles';
import PrimaryButton from '../../../core/components/buttons/primary-button.component';
import Column from '../../../core/components/column/column.component';
import { MainAxisAlignment } from '../../../core/components/flex/flex.component';
import Row from '../../../core/components/row/row.component';
import ActivityIndicator from '../../../core/components/activity-indicator/activity-indicator.component';
import { IsMobileContext } from '../../../core/context/is-mobile';
import StyleConstants from '../../../core/styles.constants';
import { getRoomDeletionImpact, type RoomDeletionImpact } from '../services/rooms.services';
import { useKeycloak } from '../../authentication/keycloak/KeycloakProvider';

interface Props {
  readonly showModal: boolean;
  readonly roomId?: string;
  readonly roomName: string;
  readonly handleClose: () => void;
  /** Called only once the impact, if any, has been acknowledged. */
  readonly handleAccept: (acknowledged: boolean) => void;
}

/**
 * Confirms a room deletion, and says what it costs before it happens.
 *
 * Deleting a room is not the small action it used to look like. Its desks are withdrawn from
 * service, every booking still standing in it from today onward is cancelled, and group
 * reservations holding desks there lose them — so this dialog states the size of that in numbers
 * and asks for an explicit acknowledgement, the same shape a desk retirement uses
 * (DESK_RETIREMENT_PLAN.md 5.3.1).
 *
 * A room drawn on a floorplan asks too, with nothing booked in it: its outline and its desks are
 * taken off the plan with it, which changes what every employee sees when they book.
 *
 * The counts come from the server, resolved in the office's own timezone. The browser cannot work
 * out where "today" starts for an office on the other side of the world, and deriving the boundary
 * here would put a different day in the dialog from the one the deletion acts on.
 */
const DeleteRoomModal = ({ showModal, roomId, roomName, handleClose, handleAccept }: Props): React.JSX.Element => {
  const isMobile = useContext(IsMobileContext);
  const { t } = useTranslation();
  const keycloak = useKeycloak();

  const [impact, setImpact] = useState<RoomDeletionImpact | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);

  useEffect(() => {
    // Guards against a late answer landing on a closed dialog, or on one reopened for a different
    // room — which would show the previous room's numbers under this room's name.
    let current = true;

    if (showModal && roomId != null) {
      setLoading(true);
      setFailed(false);
      setAcknowledged(false);

      getRoomDeletionImpact(roomId, keycloak)
        .then((result) => {
          if (!current) return;
          setImpact(result);
          // A failed count is reported as a failure, never substituted with zero: "nothing will be
          // cancelled" and "we could not find out" are different sentences, and only one of them
          // makes it safe to press delete.
          setFailed(result === undefined);
        })
        .catch(() => {
          if (current) setFailed(true);
        })
        .finally(() => {
          if (current) setLoading(false);
        });
    }

    return (): void => {
      current = false;
    };
  }, [showModal, roomId, keycloak]);

  const hasImpact = impact?.hasImpact ?? false;
  const confirmable = !loading && !failed && (!hasImpact || acknowledged);

  /** What the checkbox makes the administrator say: bookings, the floorplan, or both. */
  const acknowledgementKey = (current: RoomDeletionImpact): string => {
    const cancelsBookings = current.bookings > 0 || current.groupReservations > 0;

    if (cancelsBookings && current.hasFloorPlanImpact) return 'delete-room-acknowledge-all';
    if (current.hasFloorPlanImpact) return 'delete-room-acknowledge-floorplan';
    return 'delete-room-acknowledge';
  };

  /**
   * The floorplan part, when the room is drawn on one. Stated apart from the bookings because it
   * is a different loss — nothing is cancelled, but the plan every employee books from changes,
   * and the editor has no undo for something that happened outside it.
   */
  const renderFloorPlanImpact = (current: RoomDeletionImpact): React.JSX.Element | null => {
    if (!current.hasFloorPlanImpact) {
      return null;
    }

    return (
      <>
        <span className={style(styles.warningText)}>
          {t('delete-room-impact-floorplan', {
            desks: current.floorPlanDesks,
            storeys: current.floorPlanStoreys.join(', '),
          })}
        </span>
        <span className={style(styles.bodyText)}>{t('delete-room-impact-floorplan-editors')}</span>
      </>
    );
  };

  const renderImpact = (): React.JSX.Element => {
    if (loading) {
      return <ActivityIndicator size={20} color={StyleConstants.GREY_COLOR} />;
    }

    if (failed || impact === undefined) {
      return <span className={style(styles.error)}>{t('delete-room-impact-unavailable')}</span>;
    }

    if (!impact.hasImpact) {
      return <span className={style(styles.bodyText)}>{t('delete-room-no-impact')}</span>;
    }

    const cancelsBookings = impact.bookings > 0 || impact.groupReservations > 0;

    return (
      <Column>
        {/* Named anything but `count`, so i18next resolves the key as written instead of looking
            for plural variants of it that the translation files do not carry. */}
        {cancelsBookings ? (
          <span className={style(styles.bodyText)}>
            {t('delete-room-impact-bookings', { bookings: impact.bookings, people: impact.people })}
          </span>
        ) : (
          <span className={style(styles.bodyText)}>{t('delete-room-no-impact')}</span>
        )}
        {impact.groupReservations > 0 && (
          <span className={style(styles.bodyText)}>
            {t('delete-room-impact-groups', {
              groups: impact.groupReservations,
              cancelled: impact.groupReservationsLosingEveryDesk,
            })}
          </span>
        )}
        {impact.desks > 0 && (
          <span className={style(styles.bodyText)}>{t('delete-room-impact-desks', { desks: impact.desks })}</span>
        )}
        {cancelsBookings && <span className={style(styles.bodyText)}>{t('delete-room-impact-notified')}</span>}
        {renderFloorPlanImpact(impact)}
        <span className={style(styles.warningText)}>{t('delete-room-cannot-be-undone')}</span>
        <label className={style(styles.bodyText)} htmlFor="delete-room-acknowledge">
          <input
            id="delete-room-acknowledge"
            type="checkbox"
            checked={acknowledged}
            onChange={(event): void => {
              setAcknowledged(event.target.checked);
            }}
          />
          {t(acknowledgementKey(impact))}
        </label>
      </Column>
    );
  };

  return (
    <Modal ariaHideApp={false} isOpen={showModal} onRequestClose={handleClose} style={styles.modalStyle(isMobile)}>
      <Column>
        <span className={style(styles.bodyText)}>
          {t('about-to-remove')} {roomName}
        </span>

        {renderImpact()}

        <Row itemSpacing={12} mainAxisAlignment={MainAxisAlignment.Stretch}>
          <div className={style(styles.button)}>
            <PrimaryButton
              fullWidth
              disabled={!confirmable}
              onClick={(): void => {
                handleAccept(hasImpact);
              }}
            >
              {t('delete')}
            </PrimaryButton>
          </div>
          <div className={style(styles.button)}>
            <PrimaryButton fullWidth onClick={handleClose}>
              {t('cancel')}
            </PrimaryButton>
          </div>
        </Row>
      </Column>
    </Modal>
  );
};

export default DeleteRoomModal;
